<script setup>
import { ref, onMounted } from 'vue'
import brandingImg from '@/assets/images/homepage/branding.png'
import workflowsImg from '@/assets/images/homepage/workflows.png'
import analyticsImg from '@/assets/images/homepage/analytics.png'

// Copy lives in i18n (app.capabilities) — visuals here, index-aligned
const activeTab = ref(0)
const tabImages = [brandingImg, workflowsImg, analyticsImg]

// Précharge les captures des onglets inactifs : au premier changement
// d'onglet, l'image est déjà en cache — pas de pop pendant le fondu.
onMounted(() => {
  tabImages.forEach((src) => {
    const img = new Image()
    img.src = src
  })
})
</script>

<template>
  <section class="py-28 bg-white border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

      <!-- Header -->
      <div class="max-w-3xl mb-12">
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('app.capabilities.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('app.capabilities.description') }}</p>
      </div>

      <!-- Tabs -->
      <div class="flex flex-wrap gap-2 mb-14 border-b border-slate-200">
        <button v-for="(img, i) in tabImages" :key="i" @click="activeTab = i"
          class="px-5 py-3 text-sm font-semibold transition-colors border-b-2 -mb-px"
          :class="activeTab === i
            ? 'border-slate-900 text-slate-900'
            : 'border-transparent text-slate-400 hover:text-slate-600'">
          {{ $t(`app.capabilities.tabs[${i}]`) }}
        </button>
      </div>

      <!-- Panel — crossfade calme : sortie discrète, entrée longue et
           décélérée avec une dérive légère, même esprit que le fondu de page -->
      <transition mode="out-in" enter-active-class="transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
        enter-from-class="opacity-0 translate-y-3" enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-opacity duration-200 ease-linear" leave-from-class="opacity-100"
        leave-to-class="opacity-0">
        <div :key="activeTab" class="grid lg:grid-cols-2 gap-16 items-center">

          <!-- Text -->
          <div class="space-y-8">
            <div>
              <span class="text-xs font-mono text-primary-600 tracking-widest uppercase block mb-4">
                {{ $t(`app.capabilities.panels[${activeTab}].preTitle`) }}
              </span>
              <h3 class="text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
                {{ $t(`app.capabilities.panels[${activeTab}].title`) }}
              </h3>
            </div>

            <p class="text-lg text-slate-600 leading-relaxed">
              {{ $t(`app.capabilities.panels[${activeTab}].description`) }}
            </p>

            <div class="flex flex-wrap gap-2 pt-2">
              <span v-for="j in 3" :key="j"
                class="px-3 py-1.5 border border-slate-200 rounded text-xs font-mono text-slate-600">
                {{ $t(`app.capabilities.panels[${activeTab}].stats[${j - 1}]`) }}
              </span>
            </div>
          </div>

          <!-- Screenshot -->
          <div class="relative rounded-lg overflow-hidden border border-slate-200">
            <div class="h-9 bg-slate-50 border-b border-slate-200 flex items-center px-4 gap-1.5">
              <div class="w-2.5 h-2.5 rounded-full bg-slate-300"></div>
              <div class="w-2.5 h-2.5 rounded-full bg-slate-300"></div>
              <div class="w-16 h-1.5 bg-slate-200 rounded-full ms-2"></div>
            </div>
            <img :src="tabImages[activeTab]" class="w-full h-auto object-cover" alt="Capability screenshot">
          </div>

        </div>
      </transition>

    </div>
  </section>
</template>
